import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppShell from "../components/AppShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { ChevronLeft, Plus, BookLock, Lock, Trash2, Camera, Mic, MicOff, FileDown } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { isNative } from "../lib/native";
import T from "../components/T";
import { lightTap, successPulse } from "../lib/haptics";

function PinGate({ onUnlock }) {
  const { t } = useTranslation();
  const [pin, setPin] = useState("");
  const [setup, setSetup] = useState(!localStorage.getItem("ss_journal_pin"));
  const [confirm, setConfirm] = useState("");

  const submit = (e) => {
    e.preventDefault();
    if (setup) {
      if (pin.length < 4) return toast.info(t("incident.pin_too_short"));
      if (pin !== confirm) return toast.error(t("incident.pin_mismatch"));
      localStorage.setItem("ss_journal_pin", pin);
      onUnlock();
    } else {
      if (pin === localStorage.getItem("ss_journal_pin")) onUnlock();
      else toast.error(t("incident.pin_wrong"));
    }
  };

  return (
    <AppShell>
      <div className="px-8 pt-24 flex flex-col items-center text-center">
        <div className="w-16 h-16 rounded-full bg-brand/10 text-brand grid place-items-center">
          <Lock className="w-7 h-7" />
        </div>
        <h1 className="font-serif text-3xl mt-4">{setup ? t("incident.set_pin_h") : t("incident.enter_pin_h")}</h1>
        <p className="text-foreground/65 mt-2 max-w-xs">
          {setup ? t("incident.set_pin_sub") : t("incident.enter_pin_sub")}
        </p>
        <form onSubmit={submit} className="w-full mt-8 space-y-3">
          <Input
            data-testid="pin-input"
            type="password"
            inputMode="numeric"
            pattern="[0-9]*"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
            placeholder={t("incident.pin")}
            className="h-14 rounded-2xl text-center text-2xl tracking-[0.4em]"
          />
          {setup && (
            <Input
              data-testid="pin-confirm-input"
              type="password"
              inputMode="numeric"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value.replace(/\D/g, "").slice(0, 8))}
              placeholder={t("incident.confirm_pin")}
              className="h-14 rounded-2xl text-center text-2xl tracking-[0.4em]"
            />
          )}
          <Button data-testid="pin-submit-btn" type="submit" className="w-full h-14 rounded-full bg-brand hover:bg-brand/90 text-white">
            {setup ? t("incident.save_unlock") : t("incident.unlock")}
          </Button>
        </form>
      </div>
    </AppShell>
  );
}

function VoiceRecorder({ onSaved, testid }) {
  const { t } = useTranslation();
  const [recording, setRecording] = React.useState(false);
  const mediaRef = React.useRef(null);
  const chunksRef = React.useRef([]);
  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        if (blob.size > 6 * 1024 * 1024) { toast.info(t("incident.voice_too_long")); return; }
        const reader = new FileReader();
        reader.onload = () => onSaved(reader.result, blob.type);
        reader.readAsDataURL(blob);
        stream.getTracks().forEach((tr) => tr.stop());
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
    } catch { toast.error(t("incident.voice_perm")); }
  };
  const stop = () => {
    mediaRef.current && mediaRef.current.state === "recording" && mediaRef.current.stop();
    setRecording(false);
  };
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={recording ? stop : start}
      className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-all ${
        recording ? "bg-brand text-white border-brand" : "bg-card border-border hover:border-brand/50"
      }`}
    >
      {recording ? (<><MicOff className="w-3.5 h-3.5" /> {t("incident.voice_stop")}</>) : (<><Mic className="w-3.5 h-3.5" /> {t("incident.voice_note")}</>)}
    </button>
  );
}

export default function IncidentLog() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [unlocked, setUnlocked] = useState(false);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", tags: "", witnesses: "" });

  const refresh = async () => {
    const { data } = await api.get("/incidents");
    setItems(data);
  };
  useEffect(() => { if (unlocked) refresh(); }, [unlocked]);

  if (!unlocked) return <PinGate onUnlock={() => setUnlocked(true)} />;

  const add = async (e) => {
    e.preventDefault();
    if (!form.title || !form.body) return;
    await api.post("/incidents", {
      title: form.title, body: form.body,
      tags: form.tags ? form.tags.split(",").map((x) => x.trim()).filter(Boolean) : [],
      witnesses: form.witnesses ? form.witnesses.split(",").map((x) => x.trim()).filter(Boolean) : [],
    });
    setForm({ title: "", body: "", tags: "", witnesses: "" });
    setOpen(false);
    toast.success(t("incident.recorded"));
    lightTap();
    refresh();
  };

  const del = async (id) => {
    if (!window.confirm(t("incident.confirm_delete"))) return;
    await api.delete(`/incidents/${id}`);
    toast(t("incident.removed"));
    refresh();
  };

  const deleteAttachment = async (incidentId, attachmentId) => {
    await api.delete(`/incidents/${incidentId}/attachments/${attachmentId}`);
    refresh();
  };

  const attachPhoto = async (incidentId, file) => {
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) return toast.info(t("incident.photo_too_big"));
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post(`/incidents/${incidentId}/attachments`, {
          kind: "photo", filename: file.name, mime: file.type, data_url: reader.result,
        });
        toast.success(t("incident.photo_attached"));
        refresh();
      } catch { toast.error(t("incident.photo_error")); }
    };
    reader.readAsDataURL(file);
  };

  const exportPDF = async () => {
    try {
      const res = await api.get("/incidents/export.pdf", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const filename = `incident-log-${new Date().toISOString().slice(0, 10)}.pdf`;

      if (isNative()) {
        try {
          const [{ Filesystem, Directory }, { Share }] = await Promise.all([
            import("@capacitor/filesystem"),
            import("@capacitor/share"),
          ]);
          const base64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const result = String(reader.result || "");
              resolve(result.includes(",") ? result.split(",")[1] : result);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
          const written = await Filesystem.writeFile({
            path: filename,
            data: base64,
            directory: Directory.Cache,
          });
          await Share.share({
            title: "Sister Signal — Incident Log",
            text: "Your private incident log (PDF).",
            url: written.uri,
            dialogTitle: "Save or share your log",
          });
          successPulse();
          return;
        } catch (nativeErr) {
          console.error("Native PDF share failed:", nativeErr);
        }
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      a.click(); URL.revokeObjectURL(url);
      successPulse();
    } catch (e) { toast.error(t("incident.pdf_error")); }
  };

  const exportText = () => {
    const out = items
      .map((i) => `## ${i.title}\nDate: ${i.created_at}\nTags: ${(i.tags || []).join(", ")}\nWitnesses: ${(i.witnesses || []).join(", ")}\n\n${i.body}\n`)
      .join("\n---\n");
    const blob = new Blob([`Sister Signal — Incident Log\n\n${out}`], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `incident-log-${new Date().toISOString().slice(0, 10)}.txt`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <AppShell>
      <div className="px-8 pt-16 pb-6" data-testid="page-incident-log">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-foreground/55 hover:text-brand mb-2">
          <ChevronLeft className="w-4 h-4" /> {t("common.back")}
        </button>
        <div className="flex items-start justify-between">
          <div>
            <p className="font-script text-2xl text-brand">{t("incident.kicker")}</p>
            <h1 className="font-serif text-4xl leading-tight"><T i18nKey="incident.h" /></h1>
            <p className="mt-2 text-foreground/65 text-sm">{t("incident.sub")}</p>
          </div>
          <BookLock className="w-7 h-7 text-brand mt-2" />
        </div>
      </div>

      <div className="px-8 flex gap-2">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="incident-add-btn" className="rounded-full bg-brand hover:bg-brand/90 text-white">
              <Plus className="w-4 h-4 mr-1" /> {t("incident.new_entry")}
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-3xl max-w-sm" aria-describedby="new-incident-desc">
            <DialogHeader><DialogTitle className="font-serif text-2xl">{t("incident.new_entry")}</DialogTitle></DialogHeader>
            <p id="new-incident-desc" className="sr-only">{t("incident.new_entry_desc")}</p>
            <form onSubmit={add} className="space-y-3 mt-2">
              <div>
                <Label className="text-xs uppercase tracking-wider">{t("incident.title")}</Label>
                <Input data-testid="incident-title-input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="h-12 rounded-2xl" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">{t("incident.what_happened")}</Label>
                <Textarea data-testid="incident-body-input" required value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="min-h-32 rounded-2xl" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">{t("incident.tags")}</Label>
                <Input data-testid="incident-tags-input" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="h-12 rounded-2xl" placeholder={t("incident.tags_placeholder")} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">{t("incident.witnesses")}</Label>
                <Input data-testid="incident-witness-input" value={form.witnesses} onChange={(e) => setForm({ ...form, witnesses: e.target.value })} className="h-12 rounded-2xl" />
              </div>
              <DialogFooter>
                <Button data-testid="incident-save-btn" type="submit" className="rounded-full bg-brand hover:bg-brand/90 text-white">{t("common.save")}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        <Button data-testid="incident-export-pdf-btn" onClick={exportPDF} variant="outline" className="rounded-full">
          <FileDown className="w-4 h-4 mr-1" /> {t("incident.export_pdf")}
        </Button>
        <Button data-testid="incident-export-btn" onClick={exportText} variant="ghost" className="rounded-full text-xs">
          {t("incident.export_txt")}
        </Button>
      </div>

      <div className="px-8 mt-5 space-y-3 pb-10">
        {items.length === 0 && (
          <div className="rounded-3xl bg-card border border-dashed border-border p-6 text-center text-foreground/60" data-testid="incidents-empty">
            {t("incident.empty")}
          </div>
        )}
        {items.map((i) => (
          <article key={i.id} className="rounded-3xl bg-card border border-border p-5" data-testid={`incident-${i.id}`}>
            <div className="flex justify-between items-start">
              <div>
                <div className="font-serif text-xl">{i.title}</div>
                <div className="text-xs text-foreground/55 mt-0.5">{new Date(i.created_at).toLocaleString()}</div>
              </div>
              <button data-testid={`incident-delete-${i.id}`} onClick={() => del(i.id)} className="text-foreground/40 hover:text-destructive">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <p className="mt-3 text-foreground/80 leading-relaxed whitespace-pre-wrap">{i.body}</p>
            {(i.tags?.length > 0) && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {i.tags.map((tag, idx) => (
                  <span key={idx} className="text-[11px] px-2 py-0.5 rounded-full bg-brand/10 text-brand">{tag}</span>
                ))}
              </div>
            )}
            {(i.attachments?.length > 0) && (
              <div className="mt-3 grid grid-cols-3 gap-2">
                {i.attachments.map((a) => (
                  <div key={a.id} className="relative rounded-2xl overflow-hidden border border-border bg-background/60" data-testid={`attachment-${a.id}`}>
                    {a.kind === "photo" ? (
                      <img src={a.data_url} alt={a.filename || "photo"} className="block w-full h-24 object-cover" />
                    ) : (
                      <audio controls src={a.data_url} className="w-full" />
                    )}
                    <button data-testid={`attachment-del-${a.id}`} onClick={() => deleteAttachment(i.id, a.id)} className="absolute top-1 right-1 w-6 h-6 grid place-items-center rounded-full bg-black/40 text-white text-[10px]">
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-3 flex flex-wrap gap-2">
              <label className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-card border border-border hover:border-brand/50 cursor-pointer" data-testid={`attach-photo-${i.id}`}>
                <Camera className="w-3.5 h-3.5" /> {t("incident.add_photo")}
                <input type="file" accept="image/*" className="hidden"
                  onChange={(e) => attachPhoto(i.id, e.target.files?.[0])} />
              </label>
              <VoiceRecorder onSaved={(dataUrl, mime) =>
                api.post(`/incidents/${i.id}/attachments`, { kind: "voice", filename: `voice-${Date.now()}.webm`, mime, data_url: dataUrl })
                  .then(refresh)
                  .catch(() => toast.error(t("incident.voice_error")))
              } testid={`attach-voice-${i.id}`} />
            </div>
          </article>
        ))}
      </div>
    </AppShell>
  );
}
